import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { ResponsiveFlex, Row, Button, TextInput } from "@narmi/design_system";
import { entities } from "byzantine";
import { calculateBulkTotals } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  MAX_CHANGE_LOG_LENGTH,
  templateVersionChangeLogSchema,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/BulkTemplate.form";
import Account from "byzantine/src/Account";
import * as formAdapters from "../../../../src/adapters";
import Container from "../../../Container";
import HeaderWithSteps from "../../../HeaderWithSteps";
import reviewStyles from "../../../transfer/ach/ReviewScreen.module.scss";
import AccountContext from "../../../contexts/AccountContext";
import {
  RowWrapper,
  getSendingCollectingRows,
  type RowType,
} from "../../../PaymentActivity/CreateBulk/ReviewBulkScreen";
import useShowCollectAmounts from "../../../PaymentActivity/CreateBulk/useShowCollectAmounts";
import { TEMPLATES_ROUTE } from "../../constants";
import TemplateReviewPaymentsDialog from "./TemplateReviewPaymentsDialog";

type TemplateReviewScreenProps = {
  isEditMode?: boolean;
};

const TemplateReviewScreen = ({
  isEditMode = false,
}: TemplateReviewScreenProps) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { clearNotifications } = useNotificationContext();
  const { accounts } = useContext(AccountContext);
  const form = entities.bulkTemplates.BulkTemplateForm.useForm();
  const { send: fetchACHCompanies } =
    entities.achCompanies.useFetchACHCompanies();
  const achCompany = entities.achCompanies.useACHCompany();
  const { createBulkTemplate } = entities.bulkTemplates.useCreateBulkTemplate();
  const { createTemplateVersion } =
    entities.bulkTemplates.useCreateTemplateVersion();

  const [reviewPaymentDialogIsOpen, setReviewPaymentDialogIsOpen] =
    useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const { values, submitForm, setValidationSchema } = form;
  const {
    title,
    fromAccount,
    secCode,
    entryDesc,
    groupPosting,
    payments,
    description,
    allowExpedite,
  } = values;
  const { sendTotalFormatted, collectTotalFormatted } =
    calculateBulkTotals(payments);
  const totalPayments = payments.length;
  const totalSendPayments = payments.filter(
    (p) => p.transactionType === "send"
  ).length;
  const totalCollectPayments = payments.filter(
    (p) => p.transactionType === "collect"
  ).length;
  const showCollectAmounts = useShowCollectAmounts(totalCollectPayments > 0);
  const senderAccountData = accounts.find(
    (account: Account) => account.id === fromAccount
  );
  const senderAccount = new Account(senderAccountData || {});

  useEffect(() => {
    fetchACHCompanies(`institution_account=${senderAccountData?.id}`);
  }, [senderAccountData, fetchACHCompanies]);

  // when user lands on this screen, set validation schema to include the change log field
  useEffect(() => {
    if (isEditMode) {
      setValidationSchema((dbblL10n) =>
        form.validationSchema.concat(templateVersionChangeLogSchema(dbblL10n))
      );
    }
  }, []);

  const handleGoBack = () => {
    clearNotifications();
    navigate(-1);
  };

  const handleClickSubmit = async () => {
    const onSuccess = () => {
      navigate(`/${TEMPLATES_ROUTE}`);
    };

    if (isEditMode && values.groupId && values.versionCount != null) {
      const { success } = await submitForm();

      if (!success) {
        return;
      }

      createTemplateVersion(values.groupId, values.versionCount, {
        onSuccess,
      });
    } else {
      createBulkTemplate({ onSuccess });
    }
  };

  const toggleReviewDialog = () => {
    setReviewPaymentDialogIsOpen(!reviewPaymentDialogIsOpen);
  };

  const renderRow = (row: RowType, index: number) => {
    const [heading, content] = row;

    return (
      <RowWrapper
        key={`row-${heading}__${index}`}
        heading={heading}
        content={content}
      />
    );
  };

  const topRows: RowType[] = getSendingCollectingRows(l10n, {
    showCollectAmounts,
    sendTotalFormatted,
    collectTotalFormatted,
    totalSendPayments,
    totalCollectPayments,
    totalPayments,
  });

  topRows.push([
    l10n.getString("label-template-details"),
    <Button
      kind="plain"
      size="m"
      onClick={toggleReviewDialog}
      testId="toggle-review-template-dialog"
    >
      {l10n.getString("view-payments-cta", {
        total: totalPayments,
      })}
      <span className="narmi-icon-chevron-right" />
    </Button>,
  ]);

  const templateRows: RowType[] = [
    [l10n.getString("label-template-name"), title],
    [
      l10n.getString("label-version"),
      isEditMode && values.versionCount != null
        ? `V${values.versionCount + 1}`
        : "V1",
    ],
    [l10n.getString("label-account"), senderAccount.getShortDescription()],
  ];

  templateRows.push([
    l10n.getString("speed-label"),
    allowExpedite
      ? l10n.getString("label-allowed-to-expedite")
      : l10n.getString("label-not-allowed-to-expedite"),
  ]);

  templateRows.push(
    [l10n.getString("label-payment-type"), secCode],
    [l10n.getString("label-purpose-payment"), entryDesc]
  );

  if (description) {
    templateRows.push([
      l10n.getString("label-international-wire-review-note"),
      description,
    ]);
  }

  if (achCompany?.company_id) {
    templateRows.push([
      l10n.getString("ach-payment-review-company"),
      `${achCompany?.company_name} / ${achCompany?.company_id}`,
    ]);
  }

  return (
    <Container>
      <ResponsiveFlex gapSize="xl">
        <HeaderWithSteps
          headerText={l10n.getString("review-template-heading")}
        />

        {isEditMode && (
          <ResponsiveFlex gapSize="m">
            <div>
              <h4 className="fontSize--l nds-sans margin--bottom--xs">
                {l10n.getString("edit-details-heading")}
              </h4>
              <p className="fontSize--s fontColor--secondary">
                {l10n.getString("edit-details-description")}
              </p>
            </div>

            <TextInput
              label={l10n.getString("change-log-label")}
              maxLength={MAX_CHANGE_LOG_LENGTH}
              {...formAdapters.input(form, "changeLog")}
            />
          </ResponsiveFlex>
        )}

        <div className={reviewStyles.reviewCardContainer}>
          <div className={reviewStyles.reviewCardSection}>
            <ResponsiveFlex gapSize="m">
              {topRows.map((row, index) => renderRow(row, index))}
            </ResponsiveFlex>
            <div className="fontSize--s fontColor--primary margin--top--xs">
              {groupPosting
                ? l10n.getString("group-posting-copy")
                : l10n.getString("review-posting-single-copy")}
            </div>
          </div>

          <div className={reviewStyles.reviewCardSectionBordered}>
            <ResponsiveFlex gapSize="m">
              <h5 className="fontSize--xs fontWeight--bold">
                {l10n.getString("label-template-details").toUpperCase()}
              </h5>
              {templateRows.map((row, index) => renderRow(row, index))}
            </ResponsiveFlex>
          </div>
        </div>

        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                label={l10n.getString("button-back")}
                onClick={handleGoBack}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label={l10n.getString("button-submit", null, "Submit")}
                onClick={handleClickSubmit}
              />
            </Row.Item>
          </Row>
        </div>
      </ResponsiveFlex>
      <TemplateReviewPaymentsDialog
        isOpen={reviewPaymentDialogIsOpen}
        handleClose={toggleReviewDialog}
        payments={payments}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
      />
    </Container>
  );
};

export default TemplateReviewScreen;
